/**
 * Candidate selection for in-memory providers. A provider returns at most
 * `req.limit` results, so with 10k people it must pick the best ones itself;
 * the orchestrator then re-ranks and highlights (§12.1 rule 6). Selection uses
 * the same scorer plus frecency (`quickScore`: no copies, no highlight
 * ranges), so a frequently used item is never cut off by a slightly better
 * spelling match, and 10k rows stay inside one frame.
 */
import { quickScore, compareRanked, buildFields } from './ranking';
import { prepareTerms } from './fuzzy';

// Fields per results ARRAY (in-memory providers hand back the same array until
// their data changes): one lookup per query instead of one per row.
const fieldsByList = new WeakMap();
function fieldsOfList(results) {
  let f = fieldsByList.get(results);
  if (!f || f.length !== results.length) {
    f = results.map(buildFields);
    fieldsByList.set(results, f);
  }
  return f;
}

/**
 * @param {object[]} results  Result objects (provider, id, title, …)
 * @param {{ terms: string[], limit: number, ctx?: object }} req
 * @param {{ emptyOrder?: (a, b) => number }} [opts]
 * @returns {object[]} at most `limit`, best first, WITHOUT score/matches
 */
export function selectTop(results, req, opts = {}) {
  const limit = Math.max(1, req.limit || 6);
  const terms = req.terms || [];
  if (!terms.length) {
    const list = opts.emptyOrder ? results.slice().sort(opts.emptyOrder) : results;
    return list.slice(0, limit);
  }
  const now = Date.now();
  const prepared = prepareTerms(terms);
  const fieldsArr = fieldsOfList(results);
  const qopts = { now, prepared, fields: null };
  const top = []; // sorted best-first: { score, ts, title, id, r }
  for (let i = 0; i < results.length; i += 1) {
    const r = results[i];
    qopts.fields = fieldsArr[i];
    const score = quickScore(r, terms, qopts);
    if (score === null) continue;
    if (top.length === limit) {
      const worst = top[limit - 1];
      if (score < worst.score) continue;
    }
    const cand = { score, ts: r.ts, title: r.title, id: r.id, r };
    if (top.length === limit) {
      if (compareRanked(cand, top[limit - 1]) >= 0) continue;
      top[limit - 1] = cand;
    } else top.push(cand);
    // Insertion step keeps the array sorted; limit is small (≤ 200).
    for (let i = top.length - 1; i > 0 && compareRanked(top[i], top[i - 1]) < 0; i -= 1) {
      const t = top[i];
      top[i] = top[i - 1];
      top[i - 1] = t;
    }
  }
  return top.map((c) => c.r);
}
